import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '@/domains/auth/services/auth.service';

export interface DashboardTab {
  id: string;
  label: string;
  icon: string;
  badge?: number;
}

interface PremiumDashboardLayoutProps {
  tabs: DashboardTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  pageTitle: string;
  roleLabel: string;
  accent?: 'teal' | 'orange' | 'blue' | 'indigo' | 'emerald' | 'amber';
  userName?: string;
  userZone?: string;
  userInitial?: string;
  mobileNavTabIds?: string[];
  children: ReactNode;
}

const ACCENT = {
  teal: {
    logo: 'bg-teal-500 text-slate-950 shadow-teal-500/20',
    active: 'bg-teal-500 text-slate-950 shadow-teal-500/10',
    badge: 'bg-teal-500/20 text-teal-400',
    mobile: 'text-teal-600',
    avatar: 'bg-teal-500 text-slate-950',
    tag: 'text-teal-400',
  },
  orange: {
    logo: 'bg-orange-500 text-slate-950 shadow-orange-500/20',
    active: 'bg-orange-500 text-slate-950 shadow-orange-500/10',
    badge: 'bg-orange-500/20 text-orange-400',
    mobile: 'text-orange-600',
    avatar: 'bg-orange-500 text-slate-950',
    tag: 'text-orange-400',
  },
  blue: {
    logo: 'bg-blue-500 text-white shadow-blue-500/20',
    active: 'bg-blue-500 text-white shadow-blue-500/10',
    badge: 'bg-blue-500/20 text-blue-400',
    mobile: 'text-blue-600',
    avatar: 'bg-blue-500 text-white',
    tag: 'text-blue-400',
  },
  indigo: {
    logo: 'bg-indigo-500 text-white shadow-indigo-500/20',
    active: 'bg-indigo-500 text-white shadow-indigo-500/10',
    badge: 'bg-indigo-500/20 text-indigo-400',
    mobile: 'text-indigo-600',
    avatar: 'bg-indigo-500 text-white',
    tag: 'text-indigo-400',
  },
  emerald: {
    logo: 'bg-emerald-500 text-white shadow-emerald-500/20',
    active: 'bg-emerald-500 text-white shadow-emerald-500/10',
    badge: 'bg-emerald-500/20 text-emerald-400',
    mobile: 'text-emerald-600',
    avatar: 'bg-emerald-500 text-white',
    tag: 'text-emerald-400',
  },
  amber: {
    logo: 'bg-amber-500 text-slate-950 shadow-amber-500/20',
    active: 'bg-amber-500 text-slate-950 shadow-amber-500/10',
    badge: 'bg-amber-500/20 text-amber-400',
    mobile: 'text-amber-600',
    avatar: 'bg-amber-500 text-slate-950',
    tag: 'text-amber-400',
  },
};

export function PremiumDashboardLayout({
  tabs,
  activeTab,
  onTabChange,
  pageTitle,
  roleLabel,
  accent = 'teal',
  userName,
  userZone,
  userInitial,
  mobileNavTabIds,
  children,
}: PremiumDashboardLayoutProps) {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const colors = ACCENT[accent];
  const mobileTabs = mobileNavTabIds
    ? tabs.filter((t) => mobileNavTabIds.includes(t.id))
    : tabs.slice(0, 4);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      /* ignore */
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user');
      setShowLogoutConfirm(false);
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-28 md:pb-8 md:pl-64 flex flex-col">
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-slate-950 text-slate-200 z-30 shadow-xl">
        <div className="p-6 border-b border-slate-900 flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl shadow-lg ${colors.logo}`}>
            O
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg tracking-tight text-white leading-none">ONAPILA</span>
            <span className={`text-[10px] font-medium tracking-widest uppercase mt-1 ${colors.tag}`}>{roleLabel}</span>
          </div>
        </div>

        <div className="p-4 flex-1 space-y-1 overflow-y-auto">
          {tabs.map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-semibold transition-all group ${
                  isSelected
                    ? `${colors.active} shadow-md`
                    : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <i className={`${tab.icon} text-base`} />
                  <span>{tab.label}</span>
                </div>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`px-2 py-0.5 text-[10px] rounded-full ${isSelected ? 'bg-slate-950/30' : colors.badge}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {userName && (
          <div className="p-4 border-t border-slate-900 bg-slate-900/40 flex items-center space-x-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs ${colors.avatar}`}>
              {userInitial ?? userName[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{userName}</p>
              <p className="text-[10px] text-slate-500 truncate">{userZone ?? 'Guinée'}</p>
            </div>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 rounded-lg transition-colors"
              title="Déconnexion"
            >
              <i className="ri-logout-box-r-line text-xs" />
            </button>
          </div>
        )}
      </aside>

      <header className="bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm px-4 py-4 md:px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`md:hidden w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${colors.logo}`}>O</div>
            <h1 className="text-sm font-bold text-slate-800 md:text-xl md:font-extrabold md:tracking-tight">{pageTitle}</h1>
          </div>
          <div className="flex items-center space-x-3">
            {userZone && (
              <div className="hidden sm:flex items-center bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600">
                <i className="ri-global-line mr-1.5 text-sm" />
                {userZone}
              </div>
            )}
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="md:hidden p-2 text-slate-400 hover:text-rose-600 transition-colors"
            >
              <i className="ri-logout-box-r-line text-base" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-6">{children}</main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-100 py-2 px-1 flex justify-around items-center z-30 shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
        {mobileTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex flex-col items-center space-y-0.5 px-2 py-1 ${
              activeTab === tab.id ? `${colors.mobile} font-bold` : 'text-slate-400'
            }`}
          >
            <i className={`${tab.icon} text-lg`} />
            <span className="text-[9px] tracking-tight">{tab.label}</span>
          </button>
        ))}
      </nav>

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-slate-100 max-w-sm w-full p-5 shadow-2xl space-y-4">
            <div className="flex items-center space-x-3 text-rose-600">
              <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center text-lg">
                <i className="ri-error-warning-line" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Fermer la session ?</h3>
                <p className="text-xs text-slate-400 mt-0.5">Vous devrez ressaisir vos identifiants ONAPILA.</p>
              </div>
            </div>
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-semibold rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function StatCard({
  icon,
  label,
  value,
  color = 'slate',
}: {
  icon: string;
  label: string;
  value: string | number;
  color?: 'teal' | 'orange' | 'blue' | 'indigo' | 'amber' | 'emerald' | 'rose' | 'slate';
}) {
  const colorMap = {
    teal: 'bg-teal-50 text-teal-600',
    orange: 'bg-orange-50 text-orange-600',
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center space-x-3.5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${colorMap[color]}`}>
        <i className={icon} />
      </div>
      <div>
        <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-slate-800 mt-0.5">{value}</p>
      </div>
    </div>
  );
}

export function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
      <i className={`${icon} text-4xl text-slate-300 mb-3 block`} />
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}

export function LoadingSpinner() {
  return (
    <div className="text-center py-12">
      <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-3 block" />
      <p className="text-sm text-slate-500">Chargement...</p>
    </div>
  );
}
